import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, ExternalLink, CalendarPlus, Share2 } from 'lucide-react';
import { downloadICS, getShareLinks } from '../utils/calendar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { CardSkeleton, ImageCardSkeleton } from '../components/ui/loading-skeleton';
import axios from 'axios';
import SEO from '../components/SEO';
import { createEventSchema, createBreadcrumbSchema } from '../utils/seoSchemas';
import { getImageUrl } from '../utils/imageUrl';
import useLang from '../hooks/useLang';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AgendaPage = () => {
  const { t, i18n } = useTranslation();
  const lang = useLang();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/events?lang=${lang}`);
        setEvents(response.data.events || []);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [lang, i18n.language]);

  // Create schemas for all upcoming events
  const eventsSchema = events.length > 0 ? {
    "@context": "https://schema.org",
    "@graph": events.slice(0, 5).map(event => createEventSchema(event))
  } : null;

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: "Accueil", path: "/" },
    { name: "Agenda", path: "/agenda" }
  ]);

  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(e => e.type === filter);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-BE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-32" data-testid="agenda-page">
      <SEO 
        title="Agenda — Prochaines Soirées Invasion Latina Bruxelles 2026"
        description="Agenda complet des soirées Invasion Latina à Bruxelles. Soirées mensuelles au Mirano Continental et Open Air. Réservez vos tickets maintenant."
        keywords="agenda soirée latina bruxelles, prochaines soirées reggaeton bruxelles, événements latinos belgique, invasion latina calendar"
        canonical="/agenda"
        structuredData={eventsSchema || breadcrumbSchema}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl sm:text-6xl tracking-tight text-[#F5F0E8] mb-3"
          >
            AGENDA
          </motion.h1>
          <p className="font-serif italic text-lg text-[#B9B2A8]">
            Tous nos événements à venir
          </p>
        </div>

        {/* Filters */}
        <div className="flex justify-center mb-10">
          <Tabs defaultValue="all" onValueChange={setFilter}>
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger 
                value="all"
                className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#060606]"
                data-testid="agenda-filter-all"
              >
                Tous
              </TabsTrigger>
              <TabsTrigger 
                value="openair"
                className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#060606]"
                data-testid="agenda-filter-openair"
              >
                Open Air
              </TabsTrigger>
              <TabsTrigger 
                value="monthly"
                className="data-[state=active]:bg-[#C9A84C] data-[state=active]:text-[#060606]"
                data-testid="agenda-filter-monthly"
              >
                Mirano
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Loading */}
        {loading && (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col lg:flex-row gap-6 bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="lg:w-1/3">
                  <ImageCardSkeleton />
                </div>
                <div className="flex-1">
                  <CardSkeleton />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Events List */}
        {!loading && <div className="space-y-6">
          {filteredEvents.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-white/5 border rounded-2xl overflow-hidden card-hover ${
                event.featured ? 'border-[#C9A84C]/40' : 'border-white/10'
              }`}
              data-testid={`event-card-${event.id}`}
            >
              <div className="flex flex-col lg:flex-row">
                {/* Image */}
                <div className="lg:w-1/3 aspect-video lg:aspect-auto relative">
                  <img
                    src={getImageUrl(event.poster_url)}
                    alt={event.title}
                    className="w-full h-full object-cover"
                  />
                  {event.featured && (
                    <Badge className="absolute top-4 left-4 bg-[#C9A84C] text-[#060606]">
                      Événement principal
                    </Badge>
                  )}
                  {event.status === 'sold_out' && (
                    <Badge className="absolute top-4 right-4 bg-[#FF3B2F] text-white">
                      SOLD OUT
                    </Badge>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-6 lg:p-8 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="border-white/20 text-[#B9B2A8] text-xs uppercase">
                        {event.type === 'openair' ? 'Open Air' : 'Soirée Mensuelle'}
                      </Badge>
                    </div>
                    <h2 className="font-display text-2xl sm:text-3xl text-[#F5F0E8] mb-2">
                      {event.title}
                    </h2>
                    {event.subtitle && (
                      <p className="font-serif italic text-[#C9A84C] mb-4">
                        {event.subtitle}
                      </p>
                    )}
                    <div className="space-y-2 text-sm text-[#B9B2A8]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#C9A84C]" />
                        <span>{formatDate(event.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#C9A84C]" />
                        <span>{event.time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#C9A84C]" />
                        <span>{event.venue}, {event.city}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 mt-6">
                    <a href={event.xceed_url} target="_blank" rel="noopener noreferrer">
                      <Button
                        className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A] font-display uppercase tracking-wider gold-glow btn-active"
                        data-testid={`event-tickets-${event.id}`}
                      >
                        Tickets <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                    <Button
                      variant="outline"
                      className="border-white/20 text-[#F5F0E8] hover:bg-white/5"
                      onClick={() => window.location.href = '/booking'}
                      data-testid={`event-vip-${event.id}`}
                    >
                      Réserver Table VIP
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="border-white/20 text-[#B9B2A8] hover:text-[#C9A84C] hover:bg-white/5"
                      onClick={() => downloadICS(event)}
                      title="Ajouter au calendrier"
                    >
                      <CalendarPlus className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="border-white/20 text-[#B9B2A8] hover:text-[#C9A84C] hover:bg-white/5"
                      onClick={() => {
                        const links = getShareLinks(event);
                        navigator.clipboard.writeText(links.copy);
                      }}
                      title="Copier le lien"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>}

        {/* Empty State */}
        {!loading && filteredEvents.length === 0 && (
          <div className="text-center py-20 text-[#B9B2A8]">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun événement trouvé pour cette catégorie.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgendaPage;
